import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
  Paper,
  Pagination,
  Link as MuiLink,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.06)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
    borderColor: theme.palette.primary.main,
  },
}));

const StyledCardMedia = styled('img')(({ theme }) => ({
  height: 280,
  width: '100%',
  objectFit: 'contain',
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.common.white, 0.8),
  transition: 'transform 0.3s ease-in-out',
  display: 'block',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const PriceChip = styled(Chip)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: theme.spacing(1, 1.5),
  backgroundColor: alpha(theme.palette.success.main, 0.15),
  color: theme.palette.success.dark,
  border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
}));

const DiscountBadge = styled(Chip)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  [isRTL ? 'left' : 'right']: theme.spacing(2),
  fontWeight: 700,
  fontSize: '1rem',
  padding: theme.spacing(1, 1.5),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
  zIndex: 1,
}));

const DisclosureBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.info.main, 0.05),
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
  borderRadius: theme.spacing(2),
}));

interface Deal {
  _id: string;
  asin: string;
  title: string;
  brand?: string;
  price?: {
    displayAmount: string;
    amount: number;
    currency: string;
    savings?: {
      amount: number;
      currency: string;
      displayAmount: string;
      percentage: number;
    };
  };
  imageUrl?: string;
  lastUpdated: string;
}

export const DealsPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const theme = useTheme();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDeals, setTotalDeals] = useState(0);
  const [partnerTag, setPartnerTag] = useState('inbal0f-20');

  const isRTL = currentLang === 'he';

  useEffect(() => {
    fetchDeals();
  }, [page]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/public/deals?page=${page}&limit=24`);
      if (response.data.success) {
        setDeals(response.data.data.deals);
        setTotalPages(response.data.data.pagination.pages);
        setTotalDeals(response.data.data.pagination.total);
        if (response.data.data.partnerTag) {
          setPartnerTag(response.data.data.partnerTag);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load deals');
      console.error('Error fetching deals:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAmazonUrl = (asin: string) => {
    return `https://www.amazon.com/dp/${asin}?tag=${partnerTag}`;
  };

  const handleDealClick = (asin: string) => {
    window.open(getAmazonUrl(asin), '_blank', 'noopener,noreferrer');
  };

  const formatPrice = (price?: { displayAmount?: string; amount?: number; currency?: string }) => {
    if (!price) return 'N/A';
    return price.displayAmount || `${price.amount || 0} ${price.currency || 'USD'}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLang === 'he' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: isRTL ? 'right' : 'left' }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 1,
          }}
        >
          {t('deals.title')}
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {t('deals.subtitle')}
        </Typography>
      </Box>

      {/* Disclosure Section */}
      <DisclosureBox sx={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: theme.palette.info.main,
            mb: 1,
            textAlign: isRTL ? 'right' : 'left',
          }}
        >
          {t('deals.disclosure')}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          paragraph
          sx={{ textAlign: isRTL ? 'right' : 'left' }}
        >
          {t('deals.disclosureText')}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            fontStyle: 'italic', 
            display: 'block', 
            mt: 1,
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          {t('deals.priceNote')}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            fontStyle: 'italic', 
            display: 'block', 
            mt: 0.5,
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          {t('deals.amazonNote')}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            textAlign: isRTL ? 'right' : 'left',
            fontWeight: 500,
          }}
        >
          {t('deals.importantNote')}
        </Typography>
      </DisclosureBox>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      ) : deals.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {t('deals.noDeals')}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {deals.map((deal) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={deal._id}>
                <StyledCard onClick={() => handleDealClick(deal.asin)}>
                  <Box sx={{ position: 'relative' }}>
                    {deal.imageUrl && (
                      <StyledCardMedia
                        src={deal.imageUrl}
                        alt={deal.title}
                        loading="lazy"
                      />
                    )}
                    {deal.price?.savings?.percentage && (
                      <DiscountBadge
                        label={`-${deal.price.savings.percentage}%`}
                        size="small"
                        isRTL={isRTL}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: 1.4,
                        minHeight: 56,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr',
                      }}
                    >
                      {deal.title}
                    </Typography>
                    {deal.brand && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: 'block', textAlign: isRTL ? 'right' : 'left' }}
                      >
                        {deal.brand}
                      </Typography>
                    )}
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <PriceChip
                        label={formatPrice(deal.price)}
                        size="small"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 1,
                          fontSize: '0.75rem',
                          textAlign: isRTL ? 'right' : 'left',
                        }}
                      >
                        {t('deals.asOf')} {formatDate(deal.lastUpdated)}
                      </Typography>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDealClick(deal.asin);
                        }}
                        sx={{
                          mt: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {t('deals.viewOnAmazon')}
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </MuiLink>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1rem',
                  },
                }}
              />
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            {totalDeals} {totalDeals === 1 ? 'deal' : 'deals'} available
          </Typography>
        </>
      )}
    </Container>
  );
};

